import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { endpoints } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Sparkline } from '@/components/ui/Sparkline';
import { Cpu, MemoryStick, Activity, HardDrive } from 'lucide-react';
import { formatBytes, formatPercent } from '@/lib/format';
import { cn } from '@/lib/utils';

const REFETCH = 5_000;

type Window = '15m' | '1h' | '6h' | '24h' | '7d';
const WINDOWS: { value: Window; label: string }[] = [
  { value: '15m', label: '15 min' },
  { value: '1h', label: '1 hour' },
  { value: '6h', label: '6 hours' },
  { value: '24h', label: '24 hours' },
  { value: '7d', label: '7 days' },
];

export function BaremetalRoute() {
  const [window, setWindow] = useState<Window>('1h');

  const nodeQ = useQuery({
    queryKey: ['proxmox', 'node'],
    queryFn: endpoints.proxmox.node,
    refetchInterval: REFETCH,
  });
  const metricsQ = useQuery({
    queryKey: ['proxmox', 'metrics', window],
    queryFn: () => endpoints.proxmox.metrics(window as '15m' | '1h' | '6h' | '24h' | '7d'),
    refetchInterval: REFETCH,
  });

  const node = nodeQ.data;
  const points = metricsQ.data?.points ?? [];
  const cpuSeries = points.map((p) => p.cpu);
  const ramSeries = points.map((p) => (p.ram_total > 0 ? (p.ram_used / p.ram_total) * 100 : 0));
  const loadSeries = points.map((p) => p.load_average);
  const iowaitSeries = points.map((p) => p.io_delay);
  const ramBytesSeries = points.map((p) => p.ram_used);

  const last = points[points.length - 1];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Baremetal</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Proxmox host CPU, memory, load average, I/O wait · {points.length} samples in window
          </p>
        </div>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w.value}
              onClick={() => setWindow(w.value)}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium border transition-colors',
                window === w.value
                  ? 'bg-accent border-border text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-accent/40',
              )}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          icon={Cpu}
          label="CPU"
          value={last ? `${last.cpu.toFixed(1)}%` : '—'}
          sub={node?.cpu ? `${node.cpu.cores} cores · ${node.cpu.model.split('@')[0]?.trim()}` : undefined}
          tone={!last ? 'default' : last.cpu < 60 ? 'success' : last.cpu < 85 ? 'warn' : 'danger'}
        />
        <KpiCard
          icon={MemoryStick}
          label="Memory"
          value={last && last.ram_total > 0 ? formatPercent((last.ram_used / last.ram_total) * 100) : '—'}
          sub={last ? `${formatBytes(last.ram_used)} / ${formatBytes(last.ram_total)}` : undefined}
        />
        <KpiCard
          icon={Activity}
          label="Load avg (1m)"
          value={last ? last.load_average.toFixed(2) : '—'}
          sub={node?.cpu ? `${node.cpu.cores}-core host` : undefined}
          tone={
            !last || !node?.cpu
              ? 'default'
              : last.load_average < node.cpu.cores * 0.6
                ? 'success'
                : last.load_average < node.cpu.cores
                  ? 'warn'
                  : 'danger'
          }
        />
        <KpiCard
          icon={HardDrive}
          label="I/O wait"
          value={last ? `${last.io_delay.toFixed(1)}%` : '—'}
          sub="time spent in I/O"
          tone={!last ? 'default' : last.io_delay < 5 ? 'success' : last.io_delay < 15 ? 'warn' : 'danger'}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>CPU %</CardTitle>
          <span className="text-xs text-muted-foreground">
            {last ? `${last.cpu.toFixed(1)}%` : '—'}
          </span>
        </CardHeader>
        <CardBody className="h-[180px] flex items-center">
          <BigSparkline values={cpuSeries} width={1100} height={150} />
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>Memory used</CardTitle>
            <span className="text-xs text-muted-foreground">
              {last ? formatBytes(last.ram_used) : '—'}
            </span>
          </CardHeader>
          <CardBody className="h-[150px] flex items-center">
            <BigSparkline values={ramBytesSeries} width={540} height={120} />
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Memory %</CardTitle>
            <span className="text-xs text-muted-foreground">
              {last && last.ram_total > 0 ? formatPercent((last.ram_used / last.ram_total) * 100) : '—'}
            </span>
          </CardHeader>
          <CardBody className="h-[150px] flex items-center">
            <BigSparkline values={ramSeries} width={540} height={120} domain={[0, 100]} />
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Load average (1 min)</CardTitle>
          <span className="text-xs text-muted-foreground">{last ? last.load_average.toFixed(2) : '—'}</span>
        </CardHeader>
        <CardBody className="h-[150px] flex items-center">
          <BigSparkline values={loadSeries} width={1100} height={120} />
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>I/O wait %</CardTitle>
          <span className="text-xs text-muted-foreground">{last ? `${last.io_delay.toFixed(1)}%` : '—'}</span>
        </CardHeader>
        <CardBody className="h-[150px] flex items-center">
          <BigSparkline values={iowaitSeries} width={1100} height={120} />
        </CardBody>
      </Card>
    </div>
  );
}

function BigSparkline({
  values,
  width,
  height,
  domain,
}: {
  values: number[];
  width: number;
  height: number;
  domain?: [number, number];
}) {
  if (values.length < 2) {
    return <div className="text-sm text-muted-foreground w-full text-center">collecting samples…</div>;
  }
  // exactOptionalPropertyTypes wants us to omit the prop entirely when undefined.
  return domain !== undefined ? (
    <Sparkline values={values} width={width} height={height} domain={domain} className="w-full" />
  ) : (
    <Sparkline values={values} width={width} height={height} className="w-full" />
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  tone = 'default',
}: {
  icon: typeof Cpu;
  label: string;
  value: string;
  sub?: string | undefined;
  tone?: 'default' | 'success' | 'warn' | 'danger';
}) {
  const toneClass = {
    default: 'text-foreground',
    success: 'text-emerald-400',
    warn: 'text-amber-400',
    danger: 'text-rose-400',
  }[tone];
  return (
    <Card>
      <CardBody className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className="uppercase tracking-wider">{label}</span>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <div className={cn('text-2xl font-semibold tabular-nums', toneClass)}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </CardBody>
    </Card>
  );
}
